/* PwaInstallPrompt — brutalist "add to home screen" CTA that matches the
 * XYX Protocol aesthetic. Renders as a slide-up panel at the bottom of the
 * viewport when the browser fires `beforeinstallprompt`, OR (on iOS Safari
 * where that event doesn't exist) shows iOS-specific install instructions.
 *
 * Hidden permanently once the user installs OR dismisses for 30 days.
 */
import React, { useEffect, useState } from "react";
import { Download, X, Share, Plus } from "lucide-react";

const DISMISS_KEY = "vault.pwa.install.dismissed_at";
const DISMISS_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    // iOS Safari uses a legacy navigator flag
    window.navigator?.standalone === true
  );
}

function isIOS() {
  if (typeof window === "undefined") return false;
  const ua = window.navigator.userAgent || "";
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
}

function dismissedRecently() {
  try {
    const v = localStorage.getItem(DISMISS_KEY);
    if (!v) return false;
    return Date.now() - parseInt(v, 10) < DISMISS_TTL_MS;
  } catch {
    return false;
  }
}

export default function PwaInstallPrompt() {
  const [installEvent, setInstallEvent] = useState(null);
  const [show, setShow] = useState(false);
  const [iosHelp, setIosHelp] = useState(false);

  useEffect(() => {
    // Already running as an installed PWA → never show.
    if (isStandalone()) return;
    if (dismissedRecently()) return;

    const onBefore = (e) => {
      e.preventDefault();
      setInstallEvent(e);
      // Delay 4s so it doesn't slap the user in the face on first paint.
      setTimeout(() => setShow(true), 4000);
    };

    window.addEventListener("beforeinstallprompt", onBefore);

    // iOS Safari doesn't fire beforeinstallprompt at all — instead show
    // the iOS-specific instructions after a delay.
    if (isIOS()) {
      const t = setTimeout(() => setShow(true), 6000);
      return () => {
        window.removeEventListener("beforeinstallprompt", onBefore);
        clearTimeout(t);
      };
    }

    return () => window.removeEventListener("beforeinstallprompt", onBefore);
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
    setShow(false);
    setIosHelp(false);
  };

  const install = async () => {
    if (installEvent) {
      installEvent.prompt();
      try {
        const choice = await installEvent.userChoice;
        if (choice?.outcome === "accepted") {
          dismiss();
        }
      } catch { /* ignore */ }
      setInstallEvent(null);
    } else if (isIOS()) {
      setIosHelp(true);
    }
  };

  if (!show) return null;

  return (
    <>
      {/* iOS help modal — opens when an iOS user taps "Install" */}
      {iosHelp && (
        <div
          className="fixed inset-0 z-[10000] bg-black/85 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
          onClick={dismiss}
          data-testid="pwa-ios-help-backdrop"
        >
          <div
            className="w-full max-w-md bg-[#050505] border-2 border-white/20 p-6 relative"
            onClick={(e) => e.stopPropagation()}
            data-testid="pwa-ios-help"
          >
            <button
              type="button"
              onClick={dismiss}
              className="absolute top-3 right-3 p-1 text-white/60 hover:text-white"
              aria-label="Close"
            >
              <X size={18} />
            </button>
            <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#ff2a8e] mb-2">
              // XYX PROTOCOL · iOS INSTALL
            </div>
            <h3 className="font-heading text-2xl font-black text-white uppercase tracking-tight mb-4">
              Add to Home Screen
            </h3>
            <ol className="space-y-3 text-sm text-white/85">
              <li className="flex items-start gap-3">
                <span className="font-mono text-[10px] text-[#ff2a8e] mt-0.5">01</span>
                <span>Tap the <Share size={14} className="inline -mt-0.5 mx-1 text-[#22d3ee]" /> <strong>Share</strong> button in Safari's bottom bar.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="font-mono text-[10px] text-[#ff2a8e] mt-0.5">02</span>
                <span>Scroll down and tap <Plus size={14} className="inline -mt-0.5 mx-1 text-[#22d3ee]" /> <strong>Add to Home Screen</strong>.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="font-mono text-[10px] text-[#ff2a8e] mt-0.5">03</span>
                <span>Tap <strong>Add</strong> in the top-right.</span>
              </li>
            </ol>
            <div className="mt-5 font-mono text-[10px] tracking-[0.2em] uppercase text-white/45">
              // ACCESS GRANTED · LAUNCH FROM YOUR HOME SCREEN
            </div>
          </div>
        </div>
      )}

      {/* Bottom slide-up install bar */}
      <div
        className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:w-[380px] z-[9998] bg-[#050505] border-2 border-white/20 overflow-hidden shadow-[0_0_40px_rgba(0,0,0,0.8)]"
        data-testid="pwa-install-bar"
        style={{ animation: "xyx-slide-up 380ms ease-out" }}
      >
        <div className="flex items-center gap-3 p-3 pr-2 border-b border-white/10 bg-gradient-to-r from-[#0a0a0a] to-[#050505]">
          <img src="/icon-96.png" alt="XYX" className="w-12 h-12 border border-white/15 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="font-mono text-[9px] tracking-[0.3em] uppercase text-[#ff2a8e]">// XYX PROTOCOL · v1.0.7</div>
            <div className="font-heading text-base font-black text-white uppercase tracking-tight truncate">Install the App</div>
            <div className="text-[11px] text-white/55 truncate">Question Everything. Trust Nothing.</div>
          </div>
          <button
            type="button"
            onClick={dismiss}
            className="p-1.5 text-white/40 hover:text-white shrink-0"
            data-testid="pwa-install-dismiss"
            aria-label="Dismiss"
          >
            <X size={16} />
          </button>
        </div>
        <div className="p-3 flex items-center gap-2">
          <button
            type="button"
            onClick={install}
            data-testid="pwa-install-btn"
            className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2.5 bg-white text-black font-mono text-[11px] uppercase tracking-widest  hover:bg-[#ff2a8e] hover:text-white transition-colors"
          >
            <Download size={13} /> {isIOS() && !installEvent ? "How to install" : "Install"}
          </button>
          <button
            type="button"
            onClick={dismiss}
            className="px-3 py-2.5 font-mono text-[10px] uppercase tracking-widest text-white/55 hover:text-white border border-white/15 "
          >
            Later
          </button>
        </div>
        <div className="px-3 pb-2.5 font-mono text-[9px] tracking-[0.2em] uppercase text-white/35">
          // ADDS XYX TO YOUR HOME SCREEN · WORKS OFFLINE
        </div>
      </div>

      <style>{`
        @keyframes xyx-slide-up {
          from { transform: translateY(120%); opacity: 0; }
          to   { transform: translateY(0);    opacity: 1; }
        }
      `}</style>
    </>
  );
}
